import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'
import type { FormTemplateSection } from '@/types'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

interface SectionTreeNode {
  section: FormTemplateSection
  children: SectionTreeNode[]
}

interface DecoratedSectionTreeNode extends SectionTreeNode {
  children: DecoratedSectionTreeNode[]
  matchCount: number
}

interface SectionTreeProps {
  sections: FormTemplateSection[]
  selectedSectionId?: string
  onSelect: (sectionId: string) => void
  onAddSection: () => void
  onRenameSection: (sectionId: string, title: string) => Promise<void> | void
  onDeleteSection: (section: FormTemplateSection) => void
  matchCounts?: Record<string, number>
  filterKeyword?: string
}

const buildTree = (sections: FormTemplateSection[]): SectionTreeNode[] => {
  const byParent: Record<string, SectionTreeNode[]> = {}

  sections
    .slice()
    .sort((a, b) => a.position - b.position)
    .forEach(section => {
      const parent = section.parentSectionId ?? '__root__'
      if (!byParent[parent]) {
        byParent[parent] = []
      }
      byParent[parent].push({ section, children: [] })
    })

  const attachChildren = (nodes: SectionTreeNode[]): SectionTreeNode[] =>
    nodes.map(node => ({
      ...node,
      children: attachChildren(byParent[node.section.id] || []),
    }))

  return attachChildren(byParent['__root__'] || [])
}

const applyMatchCounts = (
  nodes: SectionTreeNode[],
  matchCounts: Record<string, number>,
): DecoratedSectionTreeNode[] =>
  nodes.map(node => {
    const decoratedChildren = applyMatchCounts(node.children, matchCounts)
    const own = matchCounts[node.section.id] ?? 0
    const childTotal = decoratedChildren.reduce((sum, child) => sum + child.matchCount, 0)
    return {
      section: node.section,
      children: decoratedChildren,
      matchCount: own + childTotal,
    }
  })

const SectionTreeItem = ({
  node,
  selectedSectionId,
  depth = 0,
  onSelect,
  isFiltering,
  onRequestDelete,
  editingSectionId,
  draftTitle,
  onStartEditing,
  onChangeDraft,
  onCommitEdit,
  onCancelEdit,
  savingSectionId,
}: {
  node: DecoratedSectionTreeNode
  selectedSectionId?: string
  depth?: number
  onSelect: (sectionId: string) => void
  isFiltering: boolean
  onRequestDelete: (section: FormTemplateSection) => void
  draftTitle: string
  onStartEditing: (section: FormTemplateSection) => void
  onChangeDraft: (value: string) => void
  onCommitEdit: (section: FormTemplateSection) => void
  onCancelEdit: () => void
  editingSectionId: string | null
  savingSectionId: string | null
}) => {
  const isEditing = editingSectionId === node.section.id
  const saving = savingSectionId === node.section.id
  const isActive = node.section.id === selectedSectionId
  const hasMatches = node.matchCount > 0
  const highlight = isFiltering && hasMatches
  const muted = isFiltering && !hasMatches
  const indentPx = depth * 16 + 8
  const actionVisibilityClass = isActive || isEditing
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onRequestDelete(node.section)
  }

  const handleSelect = () => {
    onSelect(node.section.id)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect()
    }
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleLabelClick = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    onSelect(node.section.id)
    onStartEditing(node.section)
  }

  const handleLabelKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      onSelect(node.section.id)
      onStartEditing(node.section)
    }
  }

  const handleInputBlur = () => {
    onCommitEdit(node.section)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onCommitEdit(node.section)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onCancelEdit()
    }
  }

  return (
    <div className="space-y-1">
      <div className="group flex items-center">
        <div
          role="button"
          tabIndex={0}
          onClick={handleSelect}
          onKeyDown={handleKeyDown}
          className={`flex min-w-0 flex-1 items-center justify-between rounded-md px-2 py-1 text-left text-sm transition-colors duration-150 ${
            highlight
              ? 'bg-primary-100 text-primary-900'
              : isActive
                  ? 'bg-primary-50 text-primary-900'
                  : 'hover:bg-gray-100'
          } ${muted ? 'text-gray-400' : ''}`}
          style={{ paddingLeft: `${indentPx}px` }}
        >
          {isEditing ? (
            <input
              ref={inputRef}
              value={isEditing ? draftTitle : node.section.title ?? ''}
              onChange={(event) => onChangeDraft(event.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              disabled={saving}
              className="w-full rounded border border-primary-300 bg-white px-2 py-1 text-sm text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="未命名節點"
            />
          ) : (
            <span
              className="truncate font-medium text-gray-700"
              onClick={handleLabelClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleLabelKeyDown}
            >
              {node.section.title || '未命名節點'}
            </span>
          )}
          {isFiltering && hasMatches && !isEditing && (
            <span className="ml-2 inline-flex items-center rounded-full bg-primary-200 px-2 py-0.5 text-[11px] font-medium text-primary-800">
              {node.matchCount}
            </span>
          )}
        </div>
        <div className={`ml-1 flex items-center gap-1 transition-opacity duration-150 ${actionVisibilityClass}`}>
          <button
            type="button"
            onClick={handleDelete}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
            aria-label={`刪除節點 ${node.section.title}`}
            title="刪除節點"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      {node.children.length > 0 && (
        <div className="space-y-1">
          {node.children.map(child => (
            <SectionTreeItem
              key={child.section.id}
              node={child}
              selectedSectionId={selectedSectionId}
              depth={depth + 1}
              onSelect={onSelect}
              isFiltering={isFiltering}
              onRequestDelete={onRequestDelete}
              editingSectionId={editingSectionId}
              draftTitle={draftTitle}
              onStartEditing={onStartEditing}
              onChangeDraft={onChangeDraft}
              onCommitEdit={onCommitEdit}
              onCancelEdit={onCancelEdit}
              savingSectionId={savingSectionId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const SectionTree = ({
  sections,
  selectedSectionId,
  onSelect,
  onAddSection,
  onRenameSection,
  onDeleteSection,
  matchCounts,
  filterKeyword,
}: SectionTreeProps) => {
  const tree = applyMatchCounts(buildTree(sections), matchCounts ?? {})
  const isFiltering = Boolean(filterKeyword && filterKeyword.trim().length > 0)
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [originalTitle, setOriginalTitle] = useState('')
  const [savingSectionId, setSavingSectionId] = useState<string | null>(null)

  useEffect(() => {
    if (!editingSectionId) {
      return
    }
    const currentSection = sections.find(section => section.id === editingSectionId)
    if (!currentSection) {
      setEditingSectionId(null)
      setDraftTitle('')
      setOriginalTitle('')
    }
  }, [sections, editingSectionId])

  const handleStartEditing = useCallback((section: FormTemplateSection) => {
    setEditingSectionId(section.id)
    setDraftTitle(section.title ?? '')
    setOriginalTitle(section.title ?? '')
  }, [])

  const handleCommitEdit = useCallback(async (section: FormTemplateSection) => {
    if (savingSectionId === section.id) {
      return
    }
    const trimmed = draftTitle.trim()
    const baseline = originalTitle.trim()
    if (!trimmed) {
      setDraftTitle(baseline)
      setEditingSectionId(null)
      return
    }
    if (trimmed === baseline) {
      setEditingSectionId(null)
      return
    }
    try {
      setSavingSectionId(section.id)
      await onRenameSection(section.id, trimmed)
      setEditingSectionId(null)
      setDraftTitle('')
      setOriginalTitle('')
    } catch (error) {
      console.error('[SectionTree] Failed to rename section', error)
    } finally {
      setSavingSectionId(null)
    }
  }, [draftTitle, onRenameSection, originalTitle, savingSectionId])

  const handleCancelEdit = useCallback(() => {
    setDraftTitle(originalTitle)
    setEditingSectionId(null)
  }, [originalTitle])

  return (
    <div className="bg-white border border-gray-200 rounded-lg flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">節點清單</h3>
        <button
          type="button"
          onClick={onAddSection}
          className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <PlusIcon className="h-4 w-4 mr-1" /> 新增節點
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {tree.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-6">
            尚未建立節點
          </div>
        ) : (
          tree.map(node => (
            <SectionTreeItem
              key={node.section.id}
              node={node}
              selectedSectionId={selectedSectionId}
              onSelect={onSelect}
              isFiltering={isFiltering}
              onRequestDelete={onDeleteSection}
              editingSectionId={editingSectionId}
              draftTitle={draftTitle}
              onStartEditing={handleStartEditing}
              onChangeDraft={(value) => setDraftTitle(value)}
              onCommitEdit={handleCommitEdit}
              onCancelEdit={handleCancelEdit}
              savingSectionId={savingSectionId}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default SectionTree
